import {
  usePlaybackIsPlaying,
  usePlaybackIsSeeking,
  usePlaybackPause,
} from '@app/core/playback/hooks/usePlayback';
import { useDownloadMediaFromSelection } from '@app/modules/logger/screens/stream/features/download-media';
import { useCanPlaySelection } from '@app/modules/logger/screens/stream/hooks/selection/useCanPlaySelection';
import { useCopyQuickShareURLFromSelection } from '@app/modules/logger/screens/stream/hooks/selection/useSelectionActions';
import { useStreamSettingsStore } from '@app/modules/logger/screens/stream/stores/stream-screen-settings.store';
import { useStreamSelection } from '@app/modules/logger/screens/stream/stores/useStreamSelection';
import { useIntl } from 'react-intl';
import { usePlaySelection } from '../../../../hooks/playback/useStreamPlayback';
import { useOpenFragmentDrawer } from '../useOpenFragmentDrawer';
import { SelectionMenuDefaultP } from './SelectionMenuDefaultP';
import { SelectionMenuAction, useSelectionMenuCapture } from './useSelectionMenuCapture';

export function SelectionMenuDefault() {
  const isSeeking = usePlaybackIsSeeking();
  const isPlaying = usePlaybackIsPlaying();
  const pause = usePlaybackPause();
  const playSelection = usePlaySelection();
  const { $t } = useIntl();
  // Only hide when create-fragment is already open; other drawers (e.g. live-topics) can be replaced.
  const isCreateFragmentDrawerOpen = useStreamSettingsStore(
    (s) => s.drawerType === 'create-fragment',
  );
  const openFragmentDrawer = useOpenFragmentDrawer();
  const { clearSelection, selectionExists, selectionRange } = useStreamSelection();
  const canPlay = useCanPlaySelection();
  const { downloadMedia, downloadAudioMedia, isPreparingVideoDownload, isPreparingAudioDownload } =
    useDownloadMediaFromSelection();
  const copyQuickShareURLFromSelection = useCopyQuickShareURLFromSelection();
  const capture = useSelectionMenuCapture();

  const durationMs = selectionExists ? selectionRange.to - selectionRange.from : undefined;

  const captureAndRun = (action: SelectionMenuAction, run: () => void) => {
    capture({ action, mode: 'default', duration_ms: durationMs });
    run();
  };

  return (
    <SelectionMenuDefaultP
      loadingAudio={isSeeking}
      play={canPlay ? () => captureAndRun('play', () => playSelection()) : null}
      pause={isPlaying ? () => captureAndRun('pause', () => pause()) : null}
      createFragment={
        isCreateFragmentDrawerOpen
          ? null
          : () => captureAndRun('create_fragment', openFragmentDrawer)
      }
      downloadMedia={downloadMedia}
      downloadAudioMedia={downloadAudioMedia}
      isPreparingVideoDownload={isPreparingVideoDownload}
      isPreparingAudioDownload={isPreparingAudioDownload}
      copyQuickShareUrl={() =>
        captureAndRun('copy_quick_share', () => copyQuickShareURLFromSelection($t))
      }
      clear={() => captureAndRun('clear', clearSelection)}
    />
  );
}
